import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { NotePencil, Barbell, CalendarBlank, TrendUp, PencilSimple, Trash } from "@phosphor-icons/react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import EditLogModal from "./EditLogModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LogbookTab({ clientId }) {
  const [logs, setLogs] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [filterWorkout, setFilterWorkout] = useState("all");
  const [filterExercise, setFilterExercise] = useState("all");
  const [progressExercise, setProgressExercise] = useState(null);
  const [progressData, setProgressData] = useState([]);
  const [editingLog, setEditingLog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [logsRes, workoutsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/logs`),
        axios.get(`${API}/clients/${clientId}/workouts`),
      ]);
      setLogs(logsRes.data);
      setWorkouts(workoutsRes.data);
    } catch {
      // Error handled silently
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchProgress = useCallback(async (exerciseName) => {
    try {
      const { data } = await axios.get(
        `${API}/clients/${clientId}/logs/progress?exercise_name=${encodeURIComponent(exerciseName)}`
      );
      setProgressData(data);
      setProgressExercise(exerciseName);
    } catch {
      // Error handled silently
    }
  }, [clientId]);

  const handleDelete = async (log) => {
    if (!window.confirm(`Eliminare la registrazione di "${log.exercise_name}" del ${log.date}?`)) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/logs/${log.id}`);
      toast.success("Log eliminato");
      fetchData();
      if (progressExercise === log.exercise_name) fetchProgress(progressExercise);
    } catch (err) {
      console.error("[LogbookTab] delete failed", err);
      toast.error("Impossibile eliminare il log");
    }
  };

  // Unique exercise names from logs
  const exerciseNames = [...new Set(logs.map((l) => l.exercise_name))].sort();
  const workoutMap = {};
  workouts.forEach((w) => { workoutMap[w.id] = w.title; });

  // Filter logs
  const filtered = logs.filter((l) => {
    if (filterWorkout !== "all" && l.workout_id !== filterWorkout) return false;
    if (filterExercise !== "all" && l.exercise_name !== filterExercise) return false;
    return true;
  });

  // Group by date
  const grouped = {};
  filtered.forEach((l) => {
    if (!grouped[l.date]) grouped[l.date] = [];
    grouped[l.date].push(l);
  });
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div data-testid="logbook-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          LOGBOOK
        </h3>
      </div>

      {/* Progress Chart */}
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h4 className="text-sm font-bold tracking-tight uppercase text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <TrendUp size={18} className="text-[#007AFF]" /> PROGRESSI CARICHI
          </h4>
          <Select value={progressExercise || "none"} onValueChange={(v) => { if (v !== "none") fetchProgress(v); else { setProgressExercise(null); setProgressData([]); } }}>
            <SelectTrigger className="w-56 bg-[#0A0A0A] border-[#262626] text-white text-sm" data-testid="progress-exercise-select">
              <SelectValue placeholder="Seleziona esercizio" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              <SelectItem value="none" className="text-white">Seleziona esercizio</SelectItem>
              {exerciseNames.map((n) => (
                <SelectItem key={n} value={n} className="text-white">{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {progressData.length > 1 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="date" stroke="#555" tick={{ fontSize: 11 }} />
              <YAxis stroke="#555" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#888' }}
              />
              <Line type="monotone" dataKey="max_weight" name="Carico max (kg)" stroke="#007AFF" strokeWidth={2} dot={{ fill: "#007AFF", r: 4 }} />
              <Line type="monotone" dataKey="estimated_1rm" name="1RM teorico (kg)" stroke="#FF9F0A" strokeWidth={2} strokeDasharray="4 3" dot={{ fill: "#FF9F0A", r: 3 }} />
              <Line type="monotone" dataKey="total_volume" name="Volume totale" stroke="#D4FF00" strokeWidth={2} dot={{ fill: "#D4FF00", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : progressExercise ? (
          <p className="text-center py-8 text-zinc-600 text-sm">
            {progressData.length === 1 ? "Servono almeno 2 registrazioni per il grafico" : "Nessuna registrazione per questo esercizio"}
          </p>
        ) : (
          <p className="text-center py-8 text-zinc-600 text-sm">Seleziona un esercizio per vedere i progressi</p>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <Select value={filterWorkout} onValueChange={setFilterWorkout}>
          <SelectTrigger className="w-48 bg-[#0A0A0A] border-[#262626] text-white text-sm" data-testid="filter-workout-select">
            <SelectValue placeholder="Tutte le schede" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all" className="text-white">Tutte le schede</SelectItem>
            {workouts.map((w) => (
              <SelectItem key={w.id} value={w.id} className="text-white">{w.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterExercise} onValueChange={setFilterExercise}>
          <SelectTrigger className="w-48 bg-[#0A0A0A] border-[#262626] text-white text-sm" data-testid="filter-exercise-select">
            <SelectValue placeholder="Tutti gli esercizi" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all" className="text-white">Tutti gli esercizi</SelectItem>
            {exerciseNames.map((n) => (
              <SelectItem key={n} value={n} className="text-white">{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Logs grouped by date */}
      {sortedDates.length > 0 ? (
        <div className="space-y-4">
          {sortedDates.map((date) => (
            <div key={date} className="bg-[#171717] border border-[#262626] rounded-lg overflow-hidden">
              <div className="px-5 py-3 border-b border-[#1E1E1E] flex items-center gap-2">
                <CalendarBlank size={16} className="text-[#007AFF]" />
                <span className="text-sm font-bold text-white">{date}</span>
                <span className="text-[11px] text-zinc-500">{grouped[date].length} esercizi registrati</span>
              </div>
              <div className="divide-y divide-[#1E1E1E]">
                {grouped[date].map((log) => (
                  <div key={log.id} className="px-5 py-3" data-testid={`log-row-${log.id}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Barbell size={14} weight="fill" className="text-[#007AFF]" />
                      <span className="text-sm font-semibold text-white">{log.exercise_name}</span>
                      <span className="text-[10px] text-zinc-600">({workoutMap[log.workout_id] || "—"})</span>
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchProgress(log.exercise_name)}
                          className="text-zinc-500 hover:text-[#007AFF] h-6 px-2 text-[10px]"
                          data-testid={`view-progress-${log.exercise_name}`}
                        >
                          <TrendUp size={12} className="mr-1" /> Progressi
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingLog(log)}
                          className="text-zinc-500 hover:text-white h-6 w-6 p-0"
                          aria-label="Modifica log"
                          data-testid={`edit-log-${log.id}`}
                        >
                          <PencilSimple size={12} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(log)}
                          className="text-zinc-500 hover:text-[#FF3B30] h-6 w-6 p-0"
                          aria-label="Elimina log"
                          data-testid={`delete-log-${log.id}`}
                        >
                          <Trash size={12} />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 pl-5 text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">
                      <span>Serie</span>
                      <span>Carico (kg)</span>
                      <span>Reps</span>
                    </div>
                    {log.sets?.map((s, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2 pl-5 py-1 text-sm">
                        <span className="text-zinc-500 text-xs">{s.set_number}</span>
                        <span className="text-[#007AFF] font-bold text-xs">{s.weight_kg || "—"} kg</span>
                        <span className="text-[#D4FF00] font-bold text-xs">{s.reps || "—"}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <NotePencil size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Nessuna registrazione nel logbook</p>
          <p className="text-zinc-600 text-sm mt-1">I log appariranno quando il cliente registra i carichi dal suo link</p>
        </div>
      )}

      {editingLog && (
        <EditLogModal
          log={editingLog}
          clientId={clientId}
          onClose={() => setEditingLog(null)}
          onSaved={() => {
            fetchData();
            if (progressExercise === editingLog.exercise_name) fetchProgress(progressExercise);
          }}
        />
      )}
    </div>
  );
}
